
import { Question } from '../types';

export const qur101TafsirTypesQuestions: Question[] = [
  {
    id: 'qur121',
    text: 'What is ta\'wīl in the context of Qur\'ānic interpretation?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Personal interpretation based on reflection', isCorrect: true },
      { id: 'b', text: 'Not legally binding', isCorrect: true },
      { id: 'c', text: 'Helpful for deriving wisdom after consulting other sources', isCorrect: true },
      { id: 'd', text: 'The primary source of interpretation', isCorrect: false }
    ],
    explanation: 'Ta\'wīl in the context of Qur\'ānic interpretation refers to personal interpretation based on reflection. It is not legally binding and is primarily for personal understanding rather than public teaching. It can help derive wisdom and insights, but only after all other sources (Qur\'ān, Sunnah, statements of Companions and Successors, Arabic language) have been considered first.',
    topic: 'qur101'
  },
  {
    id: 'qur122',
    text: 'What are the main categories of tafsīr?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Tafsīr bi al-Ma\'thūr (based on transmission/riwāyah)', isCorrect: true },
      { id: 'b', text: 'Tafsīr bi al-Ra\'y (based on reason/dirāyah)', isCorrect: true },
      { id: 'c', text: 'Tafsīr al-Ishārī (reflective/spiritual)', isCorrect: true },
      { id: 'd', text: 'Tafsīr al-Mutlaq (absolute interpretation)', isCorrect: false }
    ],
    explanation: 'The main categories of tafsīr are: (1) Tafsīr bi al-Ma\'thūr, based on transmission (riwāyah) from authenticated sources like the Qur\'ān, Sunnah, and statements of Companions; (2) Tafsīr bi al-Ra\'y, based on reason and analysis (dirāyah) using different sciences and principles; and (3) Tafsīr al-Ishārī, reflective or spiritual interpretation based on personal reflection while adhering to established principles.',
    topic: 'qur101'
  },
  {
    id: 'qur123',
    text: 'What characterizes Tafsīr bi al-Ma\'thūr (Riwāyah)?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Based on textual transmission', isCorrect: true },
      { id: 'b', text: 'Relies on ḥadīth and historical accounts', isCorrect: true },
      { id: 'c', text: 'Focuses on events and contextual understanding', isCorrect: true },
      { id: 'd', text: 'Primarily developed in the modern era', isCorrect: false }
    ],
    explanation: 'Tafsīr bi al-Ma\'thūr (Riwāyah) is characterized by: being based on textual transmission rather than personal reasoning; relying heavily on ḥadīth and historical accounts; and focusing on events and contextual understanding of revelation. It is considered the most authoritative form of tafsīr and was the primary approach in early Islamic scholarship, not a modern development.',
    topic: 'qur101'
  },
  {
    id: 'qur124',
    text: 'What characterizes Tafsīr bi al-Ra\'y (Dirāyah)?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Based on analysis using different sciences and principles', isCorrect: true },
      { id: 'b', text: 'More technical in approach', isCorrect: true },
      { id: 'c', text: 'Uses one\'s own ijtihād to understand verses', isCorrect: true },
      { id: 'd', text: 'Can contradict established principles of sharī\'ah', isCorrect: false }
    ],
    explanation: 'Tafsīr bi al-Ra\'y (Dirāyah) is characterized by: being based on analysis using different sciences and principles; taking a more technical approach to interpretation; and using one\'s own ijtihād (scholarly reasoning) to understand verses. However, valid Tafsīr bi al-Ra\'y cannot contradict established principles of sharī\'ah—it must remain within the boundaries of Islamic orthodoxy while applying reasoned analysis.',
    topic: 'qur101'
  },
  {
    id: 'qur125',
    text: 'What characterizes Tafsīr al-Ishārī?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Based on personal reflection and spiritual insight', isCorrect: true },
      { id: 'b', text: 'Cannot contradict the apparent meaning of the text', isCorrect: true },
      { id: 'c', text: 'Not used for deriving legal rulings', isCorrect: true },
      { id: 'd', text: 'Considered more authoritative than Tafsīr bi al-Ma\'thūr', isCorrect: false }
    ],
    explanation: 'Tafsīr al-Ishārī is characterized by: being based on personal reflection and spiritual insight; providing deeper meanings while not contradicting the apparent meaning of the text; and being used primarily for spiritual growth rather than deriving legal rulings. It is considered less authoritative than Tafsīr bi al-Ma\'thūr and must conform to established principles of faith and practice.',
    topic: 'qur101'
  },
  {
    id: 'qur126',
    text: 'What are unreliable sources of tafsīr?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Isrā\'īliyyāt (Israelite narratives)', isCorrect: true },
      { id: 'b', text: 'Unhinged interpretations that ignore established principles', isCorrect: true },
      { id: 'c', text: 'Philosophical approaches that use the Qur\'ān to push an agenda', isCorrect: true },
      { id: 'd', text: 'Interpretations based on Arabic linguistic analysis', isCorrect: false }
    ],
    explanation: 'Unreliable sources of tafsīr include: Isrā\'īliyyāt (Israelite narratives), which often contradict Islamic teachings; unhinged interpretations like those of the hyper-literalist Mu\'tazilah or the everything-is-figurative Bāṭiniyyah; and philosophical approaches that exploit religion to push an agenda rather than seeking to understand the divine intent. Arabic linguistic analysis, when properly applied, is a valid aspect of tafsīr, not an unreliable source.',
    topic: 'qur101'
  },
  {
    id: 'qur127',
    text: 'How should Isrā\'īliyyāt (Israelite narratives) be treated in tafsīr?',
    type: 'single',
    options: [
      { id: 'a', text: 'Mostly ignored, especially if contradictory to Islamic teachings', isCorrect: true },
      { id: 'b', text: 'Treated as primary sources of explanation', isCorrect: false },
      { id: 'c', text: 'Given equal weight to ḥadīth narratives', isCorrect: false },
      { id: 'd', text: 'Used extensively to fill in details of prophetic stories', isCorrect: false }
    ],
    explanation: 'Isrā\'īliyyāt (Israelite narratives) should mostly be ignored in tafsīr, especially if they contradict Islamic teachings. While they occasionally add non-contradictory context, they should always be taken with significant skepticism. They are not primary sources, should never be given equal weight to authentic ḥadīth, and should not be used extensively to fill in details of prophetic stories.',
    topic: 'qur101'
  },
  {
    id: 'qur128',
    text: 'What are the prerequisites for becoming a mufassir (Qur\'ānic exegete)?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Mastery of Arabic language sciences', isCorrect: true },
      { id: 'b', text: 'Knowledge of Qur\'ānic sciences', isCorrect: true },
      { id: 'c', text: 'Sound intention and character', isCorrect: true },
      { id: 'd', text: 'Political authority or position', isCorrect: false }
    ],
    explanation: 'Prerequisites for becoming a mufassir include: mastery of Arabic language sciences (grammar, morphology, rhetoric, etc.); knowledge of Qur\'ānic sciences (readings, abrogation, circumstances of revelation, etc.); and sound intention and character. Political authority or position is not a prerequisite—what matters is knowledge, character, and divine guidance (tawfīq from Allāh).',
    topic: 'qur101'
  },
  {
    id: 'qur129',
    text: 'According to Imam al-Suyūṭī, what character traits should a mufassir possess?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Good intentions and pure purpose', isCorrect: true },
      { id: 'b', text: 'Excellent character', isCorrect: true },
      { id: 'c', text: 'Humility and gentleness', isCorrect: true },
      { id: 'd', text: 'Strictness and rigidity in applying rules', isCorrect: false }
    ],
    explanation: 'According to Imam al-Suyūṭī, a mufassir should possess: good intentions and pure purpose; excellent character (as they are in a position of mentorship); and humility, gentleness, and easiness (not strictness and rigidity). Knowledge should soften the heart and make one more lenient, not strict and harsh. Those with only a little knowledge tend to become very strict, while the truly knowledgeable are more balanced.',
    topic: 'qur101'
  },
  {
    id: 'qur130',
    text: 'Why do many mufassirs only complete their works late in life?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Each required science is a lifetime achievement on its own', isCorrect: true },
      { id: 'b', text: 'By the time one is properly qualified, one is often elderly', isCorrect: true },
      { id: 'c', text: 'The depth of knowledge required takes decades to acquire', isCorrect: true },
      { id: 'd', text: 'Writing tafsīr is traditionally forbidden until age 50', isCorrect: false }
    ],
    explanation: 'Many mufassirs only complete their works late in life because: each of the required sciences (Arabic, Qur\'ānic sciences, etc.) is a lifetime achievement on its own; by the time one is properly qualified in all these areas, one is often elderly; and the depth of knowledge required takes decades to acquire. There is no traditional age restriction, but the extensive qualification process naturally means most scholars reach maturity in their understanding later in life.',
    topic: 'qur101'
  }
];
